import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import type { Project } from '@/services/projectTypes'
import { respondentBaseService } from '@/services/respondentBaseService'

type Props = {
  respondentIds: number[]
  onClose: () => void
  onSent: (summary: string) => void
  onError: (message: string) => void
}

/** Pick one launched study and email the selected people a personal link to it. */
export default function InviteToStudyDialog({ respondentIds, onClose, onSent, onError }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('respondents')

  const [studies, setStudies] = useState<Project[] | null>(null)
  const [picked, setPicked] = useState<number | null>(null)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    let cancelled = false
    projectService
      .getProjects()
      .then((all) => {
        if (cancelled) return
        // Drafts have no live link yet; an invite to one would land on a dead page.
        const launched = all.filter((p) => p.status !== 'draft')
        setStudies(launched)
        if (launched.length === 1) setPicked(launched[0].id)
      })
      .catch(() => !cancelled && setStudies([]))
    return () => {
      cancelled = true
    }
  }, [])

  const send = async () => {
    if (picked === null) return
    setSending(true)
    try {
      const res = await respondentBaseService.invite(picked, { respondent_ids: respondentIds })
      const parts = [t('inviteDialog.result', { sent: res.sent, skipped: res.skipped })]
      if (res.failed.length) parts.push(t('inviteDialog.failed', { emails: res.failed.join(', ') }))
      onSent(parts.join(' '))
    } catch (err) {
      onError(err instanceof Error ? err.message : t('errors.invite'))
      setSending(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('inviteDialog.title', { count: respondentIds.length })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: 14, color: c.ink56 }}>{t('inviteDialog.pick')}</Typography>
        {studies === null ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : studies.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: c.ink72, py: 2 }}>{t('inviteDialog.noStudies')}</Typography>
        ) : (
          <Box role="radiogroup" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {studies.map((study) => {
              const on = picked === study.id
              return (
                <Box
                  key={study.id}
                  role="radio"
                  aria-checked={on}
                  tabIndex={0}
                  onClick={() => setPicked(study.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setPicked(study.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    bgcolor: on ? alpha(c.coral, 0.06) : 'transparent',
                    boxShadow: on ? `inset 0 0 0 2px ${c.coral}` : `inset 0 0 0 1px ${c.ink08}`,
                    transition: 'background-color 100ms ease-out, box-shadow 100ms ease-out',
                    '&:hover': { bgcolor: on ? alpha(c.coral, 0.06) : c.ink04 },
                    '&:active': { transform: 'scale(0.99)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      flex: 'none',
                      boxShadow: on ? `inset 0 0 0 5px ${c.coral}` : `inset 0 0 0 1.5px ${c.ink32}`,
                    }}
                  />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                    {study.name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: c.ink48, whiteSpace: 'nowrap' }}>
                    {t('inviteDialog.participants', { count: study.participants_count })}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={sending}>
          {t('single.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void send()} disabled={sending || picked === null}>
          {sending ? <CircularProgress size={18} color="inherit" /> : t('inviteDialog.send')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
